// Context for the "Slow response" warning (see axios.ts). Its duration is
// wall-clock time in the browser, which also counts time the tab spent hidden
// or the laptop spent asleep, and time spent waiting on a connection that died
// while it slept. Almost all slow warnings land right after a socket
// reconnect, while the same requests take well under a second on the server
// (DORA-305). So each warning carries enough to tell those cases apart from a
// genuinely slow server.

const MAX_ENTRIES = 300;
const RESYNC_WINDOW_MS = 60_000;

let lastHiddenAt = -Infinity;
let lastResyncAt = -Infinity;
const resourceEntries: PerformanceResourceTiming[] = [];

export const startRequestTimingTracking = () => {
  if (document.hidden) {
    lastHiddenAt = performance.now();
  }
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      lastHiddenAt = performance.now();
    }
  });
  // An observer rather than performance.getEntriesByName: the browser's own
  // resource timing buffer stops recording once it holds 250 entries, which a
  // dispatcher tab passes within minutes.
  if (typeof PerformanceObserver === "undefined") {
    return;
  }
  new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
      if (
        entry.initiatorType === "xmlhttprequest" ||
        entry.initiatorType === "fetch"
      ) {
        resourceEntries.push(entry);
      }
    }
    resourceEntries.splice(0, resourceEntries.length - MAX_ENTRIES);
  }).observe({ type: "resource" });
};

// Called when the board is reloaded after a socket reconnect, so requests
// started shortly after can be told apart in the warning.
export const markResync = () => {
  lastResyncAt = performance.now();
};

const round = (ms: number) => Math.round(ms);

// Splits a request's time, all on the performance.now() timeline:
// - queuedMs: from axios sending it until the browser started on it
// - blockedMs: waiting for a connection (DNS, TCP, TLS, or a stalled one)
// - serverMs: request sent until the first response byte (server + network)
// - downloadMs: first byte until the last
// - handlingMs: last byte until axios saw it (the main thread was busy)
export const timingBreakdown = (
  entry: Pick<
    PerformanceResourceTiming,
    | "startTime"
    | "connectStart"
    | "connectEnd"
    | "requestStart"
    | "responseStart"
    | "responseEnd"
  >,
  requestStartedAt: number,
  responseHandledAt: number
) => ({
  queuedMs: round(entry.startTime - requestStartedAt),
  // requestStart is 0 when the browser withheld the detailed timings.
  blockedMs: entry.requestStart
    ? round(entry.requestStart - entry.startTime)
    : null,
  newConnection: entry.connectEnd > entry.connectStart,
  serverMs: entry.requestStart
    ? round(entry.responseStart - entry.requestStart)
    : null,
  downloadMs: round(entry.responseEnd - entry.responseStart),
  handlingMs: round(responseHandledAt - entry.responseEnd),
});

const findEntry = (url: string, requestStartedAt: number) => {
  const name = new URL(url, window.location.href).href;
  for (let i = resourceEntries.length - 1; i >= 0; i--) {
    const entry = resourceEntries[i];
    // 1ms of slack: the two clocks are read at slightly different moments.
    if (entry.name === name && entry.startTime >= requestStartedAt - 1) {
      return entry;
    }
  }
  return null;
};

export const describeSlowRequest = ({
  url,
  requestStartedAt,
  responseHandledAt,
  hiddenAtStart,
}: {
  url: string;
  requestStartedAt: number;
  responseHandledAt: number;
  hiddenAtStart: boolean;
}) => {
  const sinceResync = requestStartedAt - lastResyncAt;
  const entry = findEntry(url, requestStartedAt);
  return {
    hiddenDuringRequest: hiddenAtStart || lastHiddenAt >= requestStartedAt,
    online: navigator.onLine,
    msSinceResync: sinceResync <= RESYNC_WINDOW_MS ? round(sinceResync) : null,
    timing: entry
      ? timingBreakdown(entry, requestStartedAt, responseHandledAt)
      : null,
  };
};
